import cx from 'classnames'
import React from 'react'
import { Field, Form } from 'react-final-form'

import { Outline, Primary } from '@cinch/oxd-components/button/src'
import ResetOnChange from '@cinch/oxd-components/final-form-reset-on-change/src'
import { BackArrow } from '@cinch/oxd-components/icons/src'
import SVG from '@cinch/oxd-components/svg/src'
import { required } from '@cinch/oxd-utils/validators/src'

import { getTimeOptions } from '../../utils/scheduler-utils'
import ButtonGroup from '../button-group'
import DateFields from '../date-fields'
import PostcodeField from '../postcode-field'

import * as styles from './booking-form.module.css'

export enum formNames {
  SITE = 'site',
  DATE = 'date',
  TIME = 'slotId',
}

export interface Site {
  siteId: number
  siteName: string
  siteDates: SiteDate[]
  distanceInMeters: number
}

export interface SiteDate {
  date: string
  appointmentsCount: number
  totalAppointmentSlotsCount: number
  timeSlots: TimeSlot[]
}

interface TimeSlot {
  timeSlot: string
  slotIds: number[]
  isNearest: boolean
}

enum formStates {
  changePostcode,
  selectDate,
  selectTime,
}

const defaultSite = {
  siteId: 0,
  siteName: '',
  siteDates: [],
  distanceInMeters: 0,
}

export interface BookingFormProps {
  i18n: {
    selectLabel: string
    milesAway: string
    select: string
    back: string
    changePostcode: string
    updatePostcode: string
    cancel: string
    timeLabel: string
    dateLabel: string
  }
  sites: Site[]
  onSubmit: (ExternalAppointmentDate: string, ExternalAppointmentSlotIds: number) => any
  onPostcodeChange: (postcode: string) => any
  onSiteSelect: (siteId: number) => any
}

const BookingForm: React.FC<BookingFormProps> = ({ i18n, sites, onSiteSelect, onPostcodeChange, onSubmit }) => {
  const { selectLabel, milesAway, select, back, changePostcode, updatePostcode, cancel, timeLabel, dateLabel } = i18n

  const [formState, setFormState] = React.useState<number>(formStates.selectDate)
  const [selectedSite, setSelectedSite] = React.useState<Site>(sites.length ? sites[0] : defaultSite)
  const [selectedDate, setSelectedDate] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (selectedDate) {
      setFormState(formStates.selectTime)
    }
  }, [selectedDate])

  const handleSiteUpdated = (siteId: number) => {
    const site = sites.find((s) => s.siteId === siteId)

    if (site) {
      setSelectedSite(site)
      onSiteSelect(site.siteId)
    }
  }

  const handleFormSubmit = (values: any) => {
    if (formState === formStates.changePostcode) {
      return onPostcodeChange(values.postcode)
    } else {
      return onSubmit(values[formNames.DATE], values[formNames.TIME])
    }
  }

  const PostcodeFields: React.FC<any> = ({ values }) => (
    <>
      <PostcodeField />
      <div className={styles.formButtons}>
        <Outline type="button" className={styles.button} onClick={() => setFormState(formStates.selectDate)}>
          {cancel}
        </Outline>
        <Primary oxdClassName={styles.button} type="submit" disabled={!values.postcode} oxford={true}>
          {updatePostcode}
        </Primary>
      </div>
    </>
  )

  const DateSection = (
    <>
      <DateFields
        sites={sites}
        distance={selectedSite.distanceInMeters}
        dates={selectedSite.siteDates}
        onDateSelected={() => setFormState(formStates.selectTime)}
        i18n={{ selectLabel, milesAway, dateLabel }}
      />
      <div className={styles.formButtons}>
        <Outline type="button" className={styles.button} onClick={() => setFormState(formStates.changePostcode)}>
          <SVG className={styles.backArrow} children={BackArrow} size={12} />
          {changePostcode}
        </Outline>
      </div>
    </>
  )

  const TimeSection: React.FC<{ invalid: boolean; submitting: boolean }> = ({ invalid, submitting }) => (
    <>
      <Field
        name={formNames.TIME}
        label={timeLabel}
        component={ButtonGroup}
        validate={required}
        options={getTimeOptions(selectedSite.siteDates, selectedDate)}
      />
      <div className={styles.formButtons}>
        <Outline type="button" className={styles.button} onClick={() => setFormState(formStates.selectDate)}>
          <SVG className={styles.backArrow} children={BackArrow} size={12} />
          {back}
        </Outline>
        <Primary oxdClassName={styles.button} isLoading={submitting} type="submit" disabled={invalid} oxford={true}>
          {select}
        </Primary>
      </div>
    </>
  )

  const render = (formProps: any) => {
    const {
      handleSubmit,
      values,
      form: { initialize },
    } = formProps

    if (values[formNames.SITE] !== selectedSite.siteId) {
      handleSiteUpdated(parseInt(values[formNames.SITE], 10))
      initialize({
        [formNames.SITE]: values[formNames.SITE],
      })
    }

    if (!!values[formNames.DATE] && values[formNames.DATE] !== selectedDate) {
      setSelectedDate(values[formNames.DATE])
    }

    let formChild: React.ReactNode = null
    if (formState === formStates.changePostcode) {
      formChild = <PostcodeFields {...formProps} />
    } else {
      formChild = selectedSite ? (
        <>
          <div className={cx({ [styles.fieldsHidden]: formState !== formStates.selectDate })}>{DateSection}</div>
          <div className={cx({ [styles.fieldsHidden]: formState !== formStates.selectTime })}>
            <TimeSection {...formProps} />
          </div>
        </>
      ) : null
    }

    return (
      <form onSubmit={handleSubmit} className={styles.form} name="booking-scheduler">
        <ResetOnChange name={formNames.SITE} fields={[{ fieldName: formNames.DATE }, { fieldName: formNames.TIME }]} />
        <ResetOnChange name={formNames.DATE} fields={[{ fieldName: formNames.TIME }]} />
        {formChild}
      </form>
    )
  }

  const initalValues = { [formNames.SITE]: selectedSite.siteId }

  return <Form onSubmit={handleFormSubmit} initialValues={initalValues} render={render} />
}

export default BookingForm
